import React, { forwardRef, useMemo } from "react";
import {
  Fade,
  Tooltip,
  TooltipProps,
  useTheme,
} from "@mui/material";

/**
 * Material Design 3 (Material You) styled Tooltip.
 *
 * Wraps the standard MUI `Tooltip` and applies the M3 visual language:
 * - 16 px corner radius (M3 extra-small surface)
 * - Soft elevation 1 shadow
 * - Poppins 12 px caption
 * - 150 ms fade transition
 * - Tonal dark background for AA contrast in both light and dark mode
 *
 * Use this as a drop-in replacement for `Tooltip` in any visual surface
 * that should feel M3. Existing `Tooltip` usages across the app are
 * intentionally left untouched to avoid regressions.
 */
const M3Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (props, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Material You tonal surface: a slightly elevated neutral in both modes.
    // Light mode → grey 900 (near-black). Dark mode → translucent white.
    const tooltipSx = useMemo(
      () =>
        ({
          borderRadius: "16px",
          bgcolor: isDark ? "rgba(255,255,255,0.92)" : theme.palette.grey[900],
          color: isDark ? theme.palette.grey[900] : "#fff",
          fontFamily: "Poppins-Light",
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.4,
          padding: "6px 12px",
          maxWidth: 280,
          boxShadow:
            "0 1px 2px rgba(0,0,0,0.30), 0 1px 3px 1px rgba(0,0,0,0.15)",
          border: `1px solid ${
            isDark ? "rgba(0,0,0,0.08)" : "rgba(0,0,0,0.12)"
          }`,
        }) as const,
      [isDark, theme]
    );

    const popperSx = useMemo(
      () =>
        ({
          // subtle zoom-in on enter for the M3 "emphasized" feel
          "[data-popper-placement] .MuiTooltip-tooltip": {
            transformOrigin: "center",
            transition: "transform 150ms ease-out",
          },
        }) as const,
      []
    );

    const arrowSx = useMemo(
      () =>
        ({
          color: isDark ? "rgba(255,255,255,0.92)" : theme.palette.grey[900],
        }) as const,
      [isDark, theme]
    );

    const {
      arrow = true,
      disableInteractive = true,
      TransitionComponent = Fade,
      TransitionProps = { timeout: 150 },
      placement = "top",
      slotProps,
      ...rest
    } = props;

    return (
      <Tooltip
        ref={ref}
        arrow={arrow}
        disableInteractive={disableInteractive}
        TransitionComponent={TransitionComponent}
        TransitionProps={TransitionProps}
        placement={placement}
        slotProps={{
          tooltip: { sx: tooltipSx, ...(slotProps?.tooltip as object) },
          popper: { sx: popperSx, ...(slotProps?.popper as object) },
          arrow: { sx: arrowSx, ...(slotProps?.arrow as object) },
        }}
        {...rest}
      />
    );
  }
);

M3Tooltip.displayName = "M3Tooltip";

export default M3Tooltip;
